import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantFromDomain } from '@/hooks/useTenantFromDomain';
import { TenantContext } from './TenantContext';
import { computeProviderDomain, setProviderDomain, setProviderSlug } from '@/lib/provider-context';
import { getCurrentDomain, getDevProviderSlug } from '@/utils/domain';

interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Provider component that resolves and provides tenant information
 */
export function TenantProvider({ children }: TenantProviderProps) {
  const tenantInfo = useTenantFromDomain();
  const { t } = useTranslation();

  // For login pages, don't block rendering if tenant info fails
  // We'll get provider info after authentication
  const pathname = window.location.pathname;
  const isLoginPage = pathname.includes('/login') || pathname.includes('/verify');
  const domainInfo = getCurrentDomain();
  const fallbackSlug = domainInfo.subdomain || getDevProviderSlug();

  if (isLoginPage) {
    // Always render login pages, even if tenant resolution fails
    // Use default/empty tenant info
    const defaultTenantInfo = tenantInfo.error ? {
      provider: null,
      branding: {},
      customScripts: null,
      isCustomDomain: false,
      isLoading: false,
      error: null,
      refreshTenant: tenantInfo.refreshTenant,
    } : tenantInfo;

    const providerDomain = computeProviderDomain({
      provider: defaultTenantInfo.provider,
      fallbackSlug,
      fallbackHostname: domainInfo.hostname,
    });
    setProviderDomain(providerDomain);
    const providerSlug = defaultTenantInfo.provider?.slug ?? fallbackSlug ?? null;
    setProviderSlug(providerSlug);

    return (
      <TenantContext.Provider value={defaultTenantInfo}>
        {children}
      </TenantContext.Provider>
    );
  }

  // For authenticated pages, show loading state while resolving tenant
  if (tenantInfo.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-muted-foreground">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if tenant resolution failed (for authenticated pages)
  if (tenantInfo.error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-semibold text-destructive mb-2">
            {t('errors.failedToLoadApplication')}
          </h1>
          <p className="text-muted-foreground mb-4">{tenantInfo.error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const resolvedDomain = computeProviderDomain({
    provider: tenantInfo.provider,
    fallbackSlug,
    fallbackHostname: domainInfo.hostname,
  });
  setProviderDomain(resolvedDomain);
  const resolvedSlug = tenantInfo.provider?.slug ?? fallbackSlug ?? null;
  setProviderSlug(resolvedSlug);

  return (
    <TenantContext.Provider value={tenantInfo}>
      {children}
    </TenantContext.Provider>
  );
}
